/**
 * Editor keyboard shortcuts (SPEC §4.6) as a pure mapping from a key press to
 * a command request, so every binding is unit-tested and every shortcut goes
 * through the Command Bus.
 *
 * Focus rules (A-18): typing in a plain text input (like search) keeps all
 * keys, including its own Ctrl+Z. Inspector fields apply their values live
 * and coalesce per edit session, so editor undo/redo still works there, but
 * tool keys don't.
 */
import type { CommandRequest } from "../../commands/types";
import { setVisibleEdit } from "../selection/selectionEdits";
import type { Edit, VisibilityTarget } from "../selection/selectionEdits";

export interface KeyLike {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

export interface KeyContext {
  /** Where keyboard focus is. */
  readonly focus: "none" | "text" | "editorField";
  /** RMB is held in the viewport, so WASD/QE fly the camera. */
  readonly flying: boolean;
  /** The active entity and whether it's visible, for H. */
  readonly active: VisibilityTarget | null;
  /** Every selected entity, which H hides or shows together. */
  readonly selected: readonly VisibilityTarget[];
  /** What Delete does to the selection (selectionEdits' deleteEdit), or null if nothing can go. */
  readonly deletion: Edit | null;
  /** What Ctrl+D does to the selection (entityCopies' duplicateEdit), or null. */
  readonly duplication: Edit | null;
  /** Whether Game View is on, for G to toggle it. */
  readonly gameView: boolean;
  /** Play in Editor, if it's running: how many zones it has and which it's on. */
  readonly playing: { readonly stops: number; readonly current: number } | null;
}

const TOOLS: Readonly<Record<string, string>> = {
  q: "select",
  w: "translate",
  e: "rotate",
  r: "scale",
};

export function shortcutFor(event: KeyLike, context: KeyContext): Edit | null {
  if (context.focus === "text") return null;
  const play = context.playing && playShortcut(event, context.playing);
  if (play) return play;

  const command = event.ctrlKey || event.metaKey;
  const lower = event.key.toLowerCase();

  if (command && lower === "z")
    return request(event.shiftKey ? "history.redo" : "history.undo", {});
  if (command && lower === "y") return request("history.redo", {});
  // Save Level, as in Unreal; it also keeps the browser's Save Page dialog away.
  if (command && lower === "s") return request("level.save", {});
  // Duplicate, as in Unreal and Unity. Only with focus on the editor itself:
  // a field keeps its own keys, and D is a fly key while RMB is held.
  if (command && lower === "d")
    return context.focus === "none" && !context.flying ? context.duplication : null;

  // Everything below is a bare key; editor fields and browser shortcuts keep theirs.
  if (context.focus === "editorField" || command || event.altKey) return null;

  if (event.key === "`" || event.key === "~") return request("panel.focus", { panel: "console" });
  if (context.flying) return null;

  const tool = TOOLS[lower];
  if (tool) return request("editor.setSetting", { key: "tool", value: tool });
  if (lower === "f") return request("camera.frame", {});
  if (lower === "g")
    return request("editor.setSetting", { key: "gameView", value: !context.gameView });
  // Delete, as in Unreal; Backspace too, since that's the key labeled delete on a Mac.
  if (event.key === "Delete" || event.key === "Backspace") return context.deletion;
  // H hides the selection, as in Unreal, or shows it when the active entity is
  // hidden, so the same key brings it back (the keyboard path to the eyes).
  if (lower === "h" && context.active) {
    return setVisibleEdit(context.selected, !context.active.visible, "keyboard");
  }

  return null;
}

/**
 * Play's own keys, as reference 05 lists them: Esc stops, 1–9 go to that zone,
 * ←/→ go to the previous or next one (the game mode wraps around).
 */
function playShortcut(
  event: KeyLike,
  playing: NonNullable<KeyContext["playing"]>,
): CommandRequest | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === "Escape") return request("play.stop", {});
  if (event.key === "ArrowLeft") return request("play.goTo", { index: playing.current - 1 });
  if (event.key === "ArrowRight") return request("play.goTo", { index: playing.current + 1 });

  const digit = Number(event.key);
  if (Number.isInteger(digit) && digit >= 1 && digit <= playing.stops) {
    return request("play.goTo", { index: digit - 1 });
  }
  return null;
}

/** Classifies the focused element for `shortcutFor`. */
export function focusKind(element: Element | null): KeyContext["focus"] {
  if (!(element instanceof HTMLElement)) return "none";
  if (element.closest("[data-editor-field]")) return "editorField";

  const editable = element.isContentEditable || element.matches("input, textarea, select");
  return editable ? "text" : "none";
}

function request(type: string, payload: object): CommandRequest {
  return { type, payload, origin: "keyboard" };
}
